import {
  useDeleteNotificationRuleMutation,
  workspaceNotificationRulesQuery,
} from "@common/api/notification-rules"
import { ConfirmDialog } from "@common/components/shared/confirm-dialog"
import { handleError } from "@common/utils"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import useCustomToast from "@/hooks/use-custom-toast"
import { requirePermission } from "@/lib/require-permission"
import { EVENTS } from "../-lib/events"
import { DELETE_RULES_PERMISSION } from "../-lib/permission"

export const Route = createFileRoute(
  "/_layout/settings/_panel/workspace/notification-rules/$ruleId/delete",
)({
  beforeLoad: ({ context: { queryClient } }) =>
    requirePermission(
      queryClient,
      DELETE_RULES_PERMISSION,
      "/settings/workspace/general",
    ),
  loader: async ({ context: { queryClient }, params: { ruleId } }) => {
    const rules = await queryClient.ensureQueryData(
      workspaceNotificationRulesQuery(),
    )
    const rule = rules.rules.find((entry) => entry.id === ruleId)
    if (!rule || rule.system_key) {
      throw redirect({
        to: "/settings/workspace/notification-rules/$ruleId",
        params: { ruleId },
        replace: true,
      })
    }
  },
  component: DeleteRuleDialog,
})

function DeleteRuleDialog(): React.ReactElement | null {
  const { ruleId } = Route.useParams()
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const mutation = useDeleteNotificationRuleMutation()
  const { data } = useSuspenseQuery(workspaceNotificationRulesQuery())
  const rule = data.rules.find((entry) => entry.id === ruleId)

  if (!rule || rule.system_key) {
    return null
  }

  const close = () =>
    navigate({
      to: "/settings/workspace/notification-rules/$ruleId",
      params: { ruleId },
    })

  const confirm = () =>
    mutation.mutate(
      { rule_id: ruleId },
      {
        onSuccess: () => {
          showToast("Deleted", `${rule.name} deleted.`, "success")
          navigate({
            to: "/settings/workspace/notification-rules",
            search: { area: EVENTS[rule.event_type].area },
            ignoreBlocker: true,
          })
        },
        onError: (error) => handleError(error, showToast),
      },
    )

  return (
    <ConfirmDialog
      title="Delete this rule?"
      description={`${rule.name} stops notifying anyone, and drops out of everyone's notification settings. Turn it off instead if you might want it back.`}
      action="Delete rule"
      pendingAction="Deleting…"
      pending={mutation.isPending}
      onConfirm={confirm}
      onCancel={close}
    />
  )
}
